import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const Icons = {
  Dashboard: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  Reviews: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  Profile: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Settings: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v6m0 6v6M4.22 4.22l4.24 4.24m2.12 2.12l4.24 4.24M1 12h6m6 0h6m-17.78 7.78l4.24-4.24m2.12-2.12l4.24-4.24" />
    </svg>
  ),
  HelpCircle: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  ),
  Website: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Logout: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  Collapse: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  Expand: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
};

const menuItems = [
  { id: 'dashboard', label: 'Dashboard',  icon: 'Dashboard', path: '/business/dashboard' },
  { id: 'reviews',   label: 'Reviews', icon: 'Reviews',   path: '/business/reviews'   },
  { id: 'profile',   label: 'Profile',    icon: 'Profile',   path: '/business/profile'   },
//   { id: 'settings',  label: 'Settings',   icon: 'Settings',  path: '/business/settings'  },
//   { id: 'help',      label: 'Help',       icon: 'HelpCircle',path: '/business/help'      },
];

const BusinessSidebar = ({ onLogout }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const initial = user?.name?.charAt(0).toUpperCase() ?? 'B';
  const isActive = (path) => location.pathname === path;

  return (
    <aside
      style={{ transition: 'width 280ms cubic-bezier(0.4,0,0.2,1)' }}
      className={`fixed left-0 top-0 h-screen z-50 flex flex-col overflow-hidden
        bg-[#1b2b4b] border-r border-white/[0.06]
        ${isCollapsed ? 'w-[72px]' : 'w-[240px]'}`}
    >
      {/* ── Brand header ── */}
      <div className="flex items-center justify-between px-4 h-16 border-b border-white/[0.06] shrink-0">
        <div
          className="flex items-center gap-3 overflow-hidden"
          style={{ opacity: isCollapsed ? 0 : 1, transition: 'opacity 200ms', width: isCollapsed ? 0 : 'auto' }}
        >
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-600 text-white font-bold text-sm">V</div>
          <span className="text-[13px] font-semibold text-white tracking-tight whitespace-nowrap">VMC Reviews</span>
        </div>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 hover:text-white hover:bg-white/[0.07] transition-colors"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? Icons.Expand : Icons.Collapse}
        </button>
      </div>

      {/* ── User pill ── */}
      <div className={`flex items-center gap-3 mx-3 mt-4 mb-2 rounded-lg p-2.5 bg-white/[0.04] border border-white/[0.05] shrink-0 overflow-hidden ${isCollapsed ? 'justify-center' : ''}`}>
        <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
          {initial}
        </div>
        {!isCollapsed && (
          <div className="overflow-hidden">
            <p className="text-[12.5px] font-semibold text-white truncate leading-tight">{user?.name}</p>
            <p className="text-[11px] text-slate-500 leading-tight">Business Owner</p>
          </div>
        )}
      </div>

      {/* ── Nav section label ── */}
      {!isCollapsed && (
        <p className="px-5 pt-4 pb-1.5 text-[10.5px] font-semibold uppercase tracking-widest text-slate-600 shrink-0">
          Menu
        </p>
      )}

      {/* ── Navigation ── */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-2 space-y-0.5">
        {menuItems.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              title={isCollapsed ? item.label : undefined}
              className={`group relative w-full flex items-center rounded-lg
                ${isCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'}
                text-left transition-colors duration-150
                ${active
                  ? 'bg-blue-600/20 text-blue-400'
                  : 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.05]'
                }`}
            >
              {/* Active left bar */}
              {active && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-blue-500" />
              )}

              {/* Icon */}
              <span className={`shrink-0 ${active ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300'} transition-colors`}>
                {Icons[item.icon]}
              </span>

              {/* Label */}
              {!isCollapsed && (
                <span className="flex flex-1 items-center justify-between overflow-hidden">
                  <span className={`text-[13px] font-medium truncate ${active ? 'text-blue-300' : ''}`}>
                    {item.label}
                  </span>
                </span>
              )}

              {/* Collapsed tooltip */}
              {isCollapsed && (
                <span className="pointer-events-none absolute left-[calc(100%+10px)] z-50 hidden group-hover:flex
                  whitespace-nowrap rounded-md bg-slate-800 border border-white/[0.08] px-2.5 py-1.5
                  text-[12px] font-medium text-white"
                >
                  {item.label}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* ── Divider ── */}
      <div className="border-t border-white/[0.06] mx-3 my-2" />

      {/* ── Visit Website Button ── */}
      <button
        onClick={() => navigate('/')}
        className={`group relative flex items-center rounded-lg
          ${isCollapsed ? 'justify-center px-0 py-2.5 mx-3' : 'gap-3 px-3 py-2.5 mx-3'}
          text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10
          text-left transition-colors duration-150 mb-2`}
        title={isCollapsed ? 'Visit Website' : undefined}
      >
        <span className="shrink-0 text-slate-500 group-hover:text-emerald-400 transition-colors">
          {Icons.Website}
        </span>

        {!isCollapsed && (
          <span className="text-[13px] font-medium">
            Visit Website
          </span>
        )}

        {isCollapsed && (
          <span className="pointer-events-none absolute left-[calc(100%+10px)] z-50 hidden group-hover:flex
            whitespace-nowrap rounded-md bg-slate-800 border border-white/[0.08] px-2.5 py-1.5
            text-[12px] font-medium text-white"
          >
            Visit Website
          </span>
        )}
      </button>

      {/* ── Logout Button ── */}
      <button
        onClick={onLogout}
        className={`group relative flex items-center rounded-lg
          ${isCollapsed ? 'justify-center px-0 py-2.5 mx-3' : 'gap-3 px-3 py-2.5 mx-3'}
          text-slate-400 hover:text-red-400 hover:bg-red-500/10
          text-left transition-colors duration-150 mb-4`}
        title={isCollapsed ? 'Logout' : undefined}
      >
        <span className="shrink-0 text-slate-500 group-hover:text-red-400 transition-colors">
          {Icons.Logout}
        </span>

        {!isCollapsed && (
          <span className="text-[13px] font-medium">
            Logout
          </span>
        )}

        {isCollapsed && (
          <span className="pointer-events-none absolute left-[calc(100%+10px)] z-50 hidden group-hover:flex
            whitespace-nowrap rounded-md bg-slate-800 border border-white/[0.08] px-2.5 py-1.5
            text-[12px] font-medium text-white"
          >
            Logout
          </span>
        )}
      </button>
    </aside>
  );
};

export default BusinessSidebar;
